import { ScrollTrigger } from 'gsap/ScrollTrigger'
import SmoothScroll from '../utils/SmoothScroll'

/**
 * Filters the see & do listing over AJAX. All markup is rendered by Timber and
 * swapped into [data-see-and-do-results] — nothing is built here.
 *
 * The category is part of the path (/see-do/category/culture/) because it is a
 * real taxonomy archive; region, station and page are query params. Every URL
 * we push is a page the server can render on its own, so a refresh, a shared
 * link or a failed request all still work.
 */
export default class SeeAndDoFilter {
	constructor() {
		this.ajaxUrl = window.startdigitalData?.ajaxUrl
		if (!this.ajaxUrl) return

		document
			.querySelectorAll('[data-see-and-do-filter]')
			.forEach(section => this.#setup(section))
	}

	#setup(section) {
		const results = section.querySelector('[data-see-and-do-results]')
		const swap = section.querySelector('[data-see-and-do-swap]')
		if (!results || !swap) return

		const state = {
			section,
			results,
			swap,
			regionSelect: section.querySelector('[data-see-and-do-region]'),
			stationSelect: section.querySelector('[data-see-and-do-station]'),
			categoryLinks: section.querySelectorAll('[data-see-and-do-category]'),
			latest: 0,
			...this.#readUrl(section),
		}

		this.#syncControls(state)
		window.history.replaceState(this.#snapshot(state), '')

		state.categoryLinks.forEach(link => {
			link.addEventListener('click', event => {
				event.preventDefault()
				state.category = link.dataset.seeAndDoCategory
				state.path = new URL(link.href).pathname
				state.paged = 1
				this.#syncControls(state)
				this.#load(state)
			})
		})

		state.regionSelect?.addEventListener('change', () => {
			state.region = this.#selected(state.regionSelect)
			state.paged = 1
			this.#load(state)
		})

		state.stationSelect?.addEventListener('change', () => {
			state.station = this.#selected(state.stationSelect)
			state.paged = 1
			this.#load(state)
		})

		section
			.querySelector('[data-see-and-do-reset]')
			?.addEventListener('click', event => {
				event.preventDefault()
				state.region = ''
				state.station = ''
				state.paged = 1
				this.#syncControls(state)
				this.#load(state)
			})

		// Pagination is re-rendered on every load, so listen on the wrapper.
		results.addEventListener('click', event => {
			const link = event.target.closest('[data-see-and-do-page]')
			if (!link) return

			event.preventDefault()
			state.paged = Number(link.dataset.seeAndDoPage)
			this.#load(state)
			new SmoothScroll().scrollTo(section, { offset: -100 })
		})

		window.addEventListener('popstate', event => {
			Object.assign(state, event.state?.seeAndDo ?? this.#readUrl(section))
			this.#syncControls(state)
			this.#load(state, false)
		})
	}

	/** The filter state described by the address bar. */
	#readUrl(section) {
		const params = new URLSearchParams(window.location.search)

		return {
			path: window.location.pathname,
			category: section.dataset.seeAndDoFilter,
			region: params.get('filter_region') ?? '',
			station: params.get('filter_station') ?? '',
			paged: Number(params.get('filter_page')) || 1,
		}
	}

	/**
	 * Pushes the state into the controls, for the state changes that do not
	 * come from the controls themselves — first paint, popstate and reset.
	 */
	#syncControls(state) {
		state.region = this.#showValue(state.regionSelect, state.region)
		state.station = this.#showValue(state.stationSelect, state.station)

		state.categoryLinks.forEach(link => {
			link.classList.toggle(
				'active',
				link.dataset.seeAndDoCategory === state.category
			)
		})
	}

	/** Selects `value` if such an option exists, then reports what stuck. */
	#showValue(select, value) {
		if (!select) return ''

		select.value = value || 'all'
		if (select.selectedIndex === -1) select.value = 'all'

		return this.#selected(select)
	}

	/** The select's current filter value. "all" means no filter. */
	#selected(select) {
		return !select || select.value === 'all' ? '' : select.value
	}

	async #load(state, push = true) {
		const request = ++state.latest
		this.#setLoading(state, true)

		try {
			// Hold the swap until the outgoing cards have finished fading, so a
			// fast response does not cut the animation short.
			const [payload] = await Promise.all([
				this.#fetchResults(state),
				new Promise(resolve => setTimeout(resolve, 200)),
			])
			if (request !== state.latest) return

			state.swap.innerHTML = payload.data.html
			this.#setLoading(state, false)

			// The page just changed height, so every scroll trigger below the
			// grid is now measuring against the old layout.
			ScrollTrigger.refresh()

			if (push) {
				window.history.pushState(this.#snapshot(state), '', this.#url(state))
			}
		} catch {
			// The URL renders the same results server side, so fall back to it.
			if (request === state.latest) window.location.href = this.#url(state)
		}
	}

	async #fetchResults(state) {
		const response = await fetch(this.#endpoint(state))
		const payload = await response.json()
		if (!payload.success) throw new Error('Filter request failed')

		return payload
	}

	#setLoading(state, loading) {
		state.results.toggleAttribute('data-loading', loading)
		state.results.setAttribute('aria-busy', String(loading))
	}

	#endpoint(state) {
		const params = new URLSearchParams({
			action: 'see_and_do_filter',
			path: state.path,
		})

		if (state.category) params.set('filter_category', state.category)
		if (state.region) params.set('filter_region', state.region)
		if (state.station) params.set('filter_station', state.station)
		if (state.paged > 1) params.set('filter_page', state.paged)

		return `${this.ajaxUrl}?${params}`
	}

	#url(state) {
		const params = new URLSearchParams()

		if (state.region) params.set('filter_region', state.region)
		if (state.station) params.set('filter_station', state.station)
		if (state.paged > 1) params.set('filter_page', state.paged)

		const query = params.toString()

		return query ? `${state.path}?${query}` : state.path
	}

	#snapshot(state) {
		const { path, category, region, station, paged } = state

		return { seeAndDo: { path, category, region, station, paged } }
	}
}
